import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useMsal } from '@azure/msal-react';
import socketService from '../services/socketService';
import { useAuth } from './AuthContext';

// Socket context interface
interface SocketContextType {
  isConnected: boolean;
  reconnect: () => void;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

const SocketContext = createContext<SocketContextType>({
  isConnected: false,
  reconnect: () => {},
  connectionStatus: 'disconnected',
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const { idToken, isAuthenticated } = useAuth();
  const { inProgress } = useMsal();

  // Effect to handle (re)connection when token changes
  useEffect(() => {
    // Only initialize socket if authentication has completed
    if (inProgress !== "none") {
      setConnectionStatus('disconnected');
      return;
    }

    // If not authenticated, disconnect and reset
    if (!isAuthenticated || !idToken) {
      setConnectionStatus('disconnected');
      socketService.disconnect();
      return;
    }

    // Set the auth token for the socketService
    socketService.setAuthToken(idToken);

    // Always disconnect and reconnect to ensure backend migration
    socketService.disconnect();
    socketService.connect();

    setConnectionStatus('connecting');

    // Attach connection handlers
    socketService.onConnectionResponse(() => {
      setIsConnected(true);
      setConnectionStatus('connected');
    });

    socketService.onError(() => {
      setIsConnected(false);
      setConnectionStatus('error');
    });

    socketService.onConnectionError(async (error: { message: string; code?: string }) => {
      setIsConnected(false);
      setConnectionStatus('error');
    });
    console.log("[SocketProvider] Effect fired: isAuthenticated =", isAuthenticated);
    return () => {};
  }, [isAuthenticated, idToken, inProgress]);

  // Optional: Periodic connection checking
  useEffect(() => {
    const checkConnection = () => {
      const connected = socketService.isConnected();
      setIsConnected(connected);
      if (connected && connectionStatus !== 'connected') {
        setConnectionStatus('connected');
      } else if (!connected && connectionStatus === 'connected') {
        setConnectionStatus('disconnected');
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [connectionStatus]);

  const reconnect = useCallback(() => {
    setConnectionStatus('connecting');
    socketService.disconnect();
    setTimeout(() => {
      socketService.connect();
    }, 1000);
  }, []);

  // Memoize the context value to avoid unnecessary rerenders
  const contextValue = useMemo(() => ({
    isConnected,
    reconnect,
    connectionStatus,
  }), [isConnected, reconnect, connectionStatus]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;